/**
 * The anticipation clock's arc (docs/DESIGN.md §6's pattern, drawn as a ring rather than a bar).
 *
 * <p>Lifted out of `ForageScreen`'s own `ForageRing` when the Training School needed the same
 * thing — a second copy of an SVG arc is exactly the drift this codebase pays for elsewhere. The
 * Training School, the skill header and the skill busy card render this one component; each
 * caller computes its own elapsed fraction and this keeps the drawing. (`ForageScreen`'s clock
 * card, the original caller, retired with the 180-minute pull — R1, skill-craft UI polish.)
 *
 * <p>CARRIES NO TEXT, and is `aria-hidden`. `ForageRing` first had the ETA inside the circle, and a
 * real-browser look at the Pixel 7 viewport killed it twice over: `~2h 41m` is wider than the ring,
 * so it broke the circle, and it restated verbatim the figure in the heading beside it. The arc is
 * the picture, the text beside it is the figure. §11.8 is satisfied because that figure is always
 * visible text elsewhere in the same row/card — never carried by the arc alone. A caller that
 * renders this with no adjacent figure is using it wrong.
 *
 * <p>THE VIEWBOX IS FIXED AND THE WRAPPER IS TOKEN-SIZED. The SVG is a unitless 58x58 coordinate
 * space scaled to 100% of its wrapper, so one geometry serves both sizes and the stroke scales with
 * it — rather than the pre-lift arrangement, where the SVG carried hardcoded `width`/`height`
 * attributes of 58 inside a wrapper the tokens sized at 60 and the two were free to disagree.
 */
export type AnticipationRingProps = {
  /** Elapsed share of the wait, 0..1. Clamped here rather than trusted: this is the component
   *  that would visibly draw the wrong thing, so a stale clock or a NaN can never escape it. */
  fraction: number;
  /** `lg` — the standalone clock card (ForageScreen). `sm` — riding inside a list row, where the
   *  ring sits in the column an action button vacated (Training School). */
  size?: 'lg' | 'sm';
};

const RADIUS = 24;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function AnticipationRing({ fraction, size = 'lg' }: AnticipationRingProps) {
  const clamped = Number.isFinite(fraction) ? Math.min(1, Math.max(0, fraction)) : 0;
  return (
    <div className={`anticipation-ring anticipation-ring-${size}`} aria-hidden="true">
      <svg viewBox="0 0 58 58">
        <circle className="anticipation-ring-track" cx="29" cy="29" r={RADIUS} />
        <circle
          className="anticipation-ring-fill"
          cx="29" cy="29" r={RADIUS}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={CIRCUMFERENCE * (1 - clamped)}
        />
      </svg>
    </div>
  );
}
